import React, { useState, useEffect, useRef } from 'react';
import styles from './profileview.module.scss';
import { googleLogout } from '@react-oauth/google';
import Link from 'next/link';
import { clearIsLoggedInCookie } from '../../utils/authCookie';
import { useRouter } from 'next/router';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useUserMaster } from '../../store/user/reducer';
import Typography from '../Typography';

interface ProfileViewProps {
  setShowAccount: (show: boolean) => void;
  setIsMobileShow: (show: boolean) => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({ setShowAccount, setIsMobileShow }) => {
  const [show, setShow] = useState(true);
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement>(null);
  const { masterUser, busy } = useUserMaster();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowAccount(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [setShowAccount]);

  const handleClosePopup = () => {
    setShow(false);
    setShowAccount(false);
    setIsMobileShow(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    clearIsLoggedInCookie();
    googleLogout();
    router.push('/');
    window.location.reload();
  };

  return (
    <>
      {show && (
        <div className={styles.profileDropdown}>
          <div className={styles.profileContainer} ref={menuRef}>
            <div className={styles.profileMenu}>
              <div className={styles.mainResponsivePopup}>
                <div className={styles.toplogin}>
                  {masterUser.email ? (
                    <div className={styles.avatarIcon} style={{ border: 'none', cursor: 'auto' }}>
                      <img
                        src={masterUser.profileImage?.url || '/avatar.svg'}
                        loading="lazy"
                        alt="Avatar"
                      />
                    </div>
                  ) : (
                    <SkeletonTheme baseColor="#202020" highlightColor="#444" borderRadius={50}>
                      <Skeleton width={40} height={40} />
                    </SkeletonTheme>
                  )}
                </div>
                <div className={styles.topUserEmail}>
                  {masterUser.email ? (
                    <div className={styles.topUserEmailDiv}>
                      <Typography variant="text-sm" color="grayDark-50">
                        {`${masterUser.firstName || '-'} ${masterUser.lastName || ''}`}
                      </Typography>
                      <Typography variant="text-sm" color="grayDark-400">
                        {masterUser.email}
                      </Typography>
                    </div>
                  ) : (
                    <div className={styles.topUserEmailDiv}>
                      <SkeletonTheme baseColor="#202020" highlightColor="#444" borderRadius={50}>
                        <Skeleton width={100} height={20} />
                      </SkeletonTheme>
                      <SkeletonTheme baseColor="#202020" highlightColor="#444" borderRadius={50}>
                        <Skeleton width={150} height={20} />
                      </SkeletonTheme>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.divider}></div>

              <Link href="/members-area?tab=myaccount">
                <a className={styles.menuItem} onClick={handleClosePopup}>
                  <div className={styles.menuIcon}>
                    <img src="/user.svg" alt="User" />
                  </div>
                  <Typography
                    variant="text-sm"
                    color="primary"
                    weight="normal"
                    className={styles.menuText}
                  >
                    My Account
                  </Typography>
                </a>
              </Link>

              <Link href="/members-area?tab=myorders">
                <a className={styles.menuItem} onClick={handleClosePopup}>
                  <div className={styles.menuIcon}>
                    <img src="/order.svg" alt="Orders" />
                  </div>
                  <Typography
                    variant="text-sm"
                    color="primary"
                    weight="normal"
                    className={styles.menuText}
                  >
                    My Orders
                  </Typography>
                </a>
              </Link>

              <Link href="/members-area?tab=mysubscriptions">
                <a className={styles.menuItem} onClick={handleClosePopup}>
                  <div className={styles.menuIcon}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.33333 5.33333C1.33333 4.59695 1.93029 4 2.66667 4H13.3333C14.0697 4 14.6667 4.59695 14.6667 5.33333V12C14.6667 12.7364 14.0697 13.3333 13.3333 13.3333H2.66667C1.93029 13.3333 1.33333 12.7364 1.33333 12V5.33333Z"
                        stroke="#61656C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M1.33333 6.66667H14.6667"
                        stroke="#61656C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 9.33333H5.33333M7.33333 9.33333H10.6667"
                        stroke="#61656C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 2.66667V4"
                        stroke="#61656C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <Typography
                    variant="text-sm"
                    color="primary"
                    weight="normal"
                    className={styles.menuText}
                  >
                    My Subscriptions
                  </Typography>
                </a>
              </Link>

              <Link href="/downloads">
                <a className={styles.menuItem} onClick={handleClosePopup}>
                  <div className={styles.menuIcon}>
                    <img src="/download-cloud.svg" alt="Downloads" />
                  </div>
                  <Typography
                    variant="text-sm"
                    color="primary"
                    weight="normal"
                    className={styles.menuText}
                  >
                    Downloads
                  </Typography>
                </a>
              </Link>

              <Link href="/members-area?tab=myaddresses">
                <a className={styles.menuItem} onClick={handleClosePopup}>
                  <div className={styles.menuIcon}>
                    <img src="/address.svg" alt="Addresses" />
                  </div>
                  <Typography
                    variant="text-sm"
                    color="primary"
                    weight="normal"
                    className={styles.menuText}
                  >
                    My Addresses
                  </Typography>
                </a>
              </Link>

              <div className={styles.divider}></div>

              <div className={styles.menuItem} onClick={handleLogout}>
                <div className={styles.menuIcon}>
                  <img src="/logout.svg" alt="Logout" />
                </div>
                <Typography
                  variant="text-sm"
                  color="primary"
                  weight="normal"
                  className={styles.menuText}
                >
                  Log out
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileView;
